import { Box, Paper, Typography } from '@mui/material';
import { INK, MUTED, WARN_ORANGE } from './colorTokens';

// Same buckets and colors as the age breakdown on the L1 / L2 / L3 tabs.
const AGE_BUCKETS = [
  { key: 'lt7', label: '< 7 days', color: '#2f9e52', track: '#e4f5e9' },
  { key: 'd7_30', label: '7–30 days', color: '#2b6cb0', track: '#e3ecf7' },
  { key: 'd30_90', label: '30–90 days', color: WARN_ORANGE, track: '#fbe9dd' },
  { key: 'd90', label: '90+ days', color: '#D74B00', track: '#fde8dc' }
];
/** Age counts before any data has loaded (same keys the server sends). */
export const EMPTY_AGE_COUNTS = { lt7: 0, d7_30: 0, d30_90: 0, d90: 0, unknown: 0 };

const UNKNOWN_BUCKET = { key: 'unknown', label: 'No opened date', color: '#757575', track: '#eeeeee' };

/**
 * Age of a list of open tickets as horizontal bars (L2 Open and SRE tabs). Click a bar to filter the list under
 * it, click again to clear (bars are plain when no onSelectBucket is given). counts: { lt7, d7_30, d30_90, d90, unknown } over open tickets, computed by the server
 * (server/utils/ageBuckets.js) together with the `ageBucket` each ticket row carries.
 */
const OpenAgeCard = ({ counts, oldestDays, selectedBucket, onSelectBucket }) => {
  const buckets = counts.unknown > 0 ? [...AGE_BUCKETS, UNKNOWN_BUCKET] : AGE_BUCKETS;
  const total = buckets.reduce((sum, bucket) => sum + (counts[bucket.key] || 0), 0);
  return (
    <Paper elevation={2} sx={{ borderRadius: 2, p: 2.5, height: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.9375rem' }}>Age of open tickets</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {total} open{oldestDays != null ? ` · oldest ${oldestDays}d` : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {buckets.map((bucket) => {
          const count = counts[bucket.key] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const selected = selectedBucket === bucket.key;
          const activate = () => { if (onSelectBucket) onSelectBucket(selected ? '' : bucket.key); };
          return (
            <Box
              key={bucket.key}
              role={onSelectBucket ? 'button' : undefined}
              tabIndex={onSelectBucket ? 0 : undefined}
              aria-pressed={onSelectBucket ? selected : undefined}
              onClick={activate}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  activate();
                }
              }}
              sx={{
                cursor: onSelectBucket ? 'pointer' : 'default',
                borderRadius: '8px',
                p: 0.75,
                outline: selected ? `2px solid ${bucket.color}` : 'none',
                outlineOffset: '-2px',
                bgcolor: selected ? bucket.track : 'transparent',
                '&:hover': { bgcolor: onSelectBucket ? bucket.track : undefined }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: INK, fontWeight: 600 }}>{bucket.label}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: MUTED }}>{count}</Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: '3px', bgcolor: bucket.track, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: bucket.color, borderRadius: '3px' }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default OpenAgeCard;
